import get from 'lodash/get';
import {
  Chip,
  ChipProps,
  Tooltip,
  TooltipProps,
} from '@mui/material';
import {
  format as formatDate,
  formatDistance,
  formatDistanceStrict,
} from 'date-fns';
import { useEffect, useMemo } from 'react';
import { FunctionField, FunctionFieldProps } from 'react-admin';
import { useInlineMemo } from '@/util/useMemoStyles';

export type GenericDateFunctionFieldProps = Omit<
  FunctionFieldProps,
  'render'
> & {
  format?: string;
  valueSource?: string;
};

export const GenericDateFunctionField = ({
  source,
  valueSource,
  format,
  ...props
}: GenericDateFunctionFieldProps) => (
  <FunctionField
    {...props}
    source={source}
    render={(rec: any) => {
      const date = get(rec, valueSource ?? source!);

      if (!date) {
        return null;
      }

      return (
        <GenericRelativeDateDisplay format={format} date={date} />
      );
    }}
  />
);

export type GenericRelativeDateDisplayProps = {
  date: Date | string | null | undefined;
  format?: string;
  chipProps?: ChipProps;
} & Omit<TooltipProps, 'children' | 'title'>;

export const GenericRelativeDateDisplay = ({
  date,
  format = 'MM/dd/yyyy hh:mm:ss a',
  chipProps,
  ...rest
}: GenericRelativeDateDisplayProps) => {
  const validDate = useMemo(() => {
    if (!date) {
      return null;
    }

    try {
      const result = new Date(date);

      if (isNaN(result.getTime())) {
        return null;
      }

      return result;
    } catch {
      return null;
    }
  }, [date]);

  useEffect(() => {
    console.log('Date', date, typeof date);
  }, [date]);

  useEffect(() => {
    console.log('Valid Date', validDate, typeof validDate);
  }, [validDate]);

  const distance = useMemo(() => {
    if (!validDate) {
      return '';
    }

    console.log('Distance', validDate);

    return formatDistance(validDate, new Date(), { addSuffix: true });
  }, [validDate]);

  const formattedDate = useMemo(() => {
    if (!validDate) {
      return '';
    }

    console.log('Formatted Date', validDate, format);

    return formatDate(validDate, format);
  }, [validDate, format]);

  return (
    <Tooltip title={distance} {...rest}>
      <Chip {...chipProps} label={formattedDate} />
    </Tooltip>
  );
};

type DateIntervalDisplayProps = {
  date1: string;
  date2: string;
  chipColor?: string;
};

export const DateIntervalDisplay = ({
  date1,
  date2,
  chipColor,
}: DateIntervalDisplayProps) => {
  const dateDistance = useMemo(() => {
    const { dateDistance, date1Date, date2Date } =
      calculateDateDistance(date1, date2);

    if (dateDistance === null) {
      return 'Invalid dates: ' + date1 + ' - ' + date2;
    }

    if (dateDistance < 60000) {
      return `${(dateDistance / 1000).toPrecision(2)}s`;
    }

    return formatDistanceStrict(date1Date, date2Date, {
      addSuffix: false,
    });
  }, [date1, date2]);

  return (
    <GenericChipDisplay label={dateDistance} chipColor={chipColor} />
  );
};

export type GenericChipDisplayProps = {
  label: string;
  chipColor?: string;
};

export const GenericChipDisplay = ({
  label,
  chipColor,
}: GenericChipDisplayProps) => (
  <Chip
    label={label}
    sx={useInlineMemo({ backgroundColor: chipColor })}
  />
);

export const calculateDateDistance = (
  date1: string,
  date2: string,
) => {
  const date1Date = new Date(date1);
  const date2Date = new Date(date2);

  if (isNaN(date1Date.getTime()) || isNaN(date2Date.getTime())) {
    return {
      dateDistance: null,
    } as const;
  }

  const dateDistance = Math.abs(
    date1Date.getTime() - date2Date.getTime(),
  );

  return { dateDistance, date1Date, date2Date } as const;
};
